/* PreNumbers PSBB — reskinned shell + context.

   Keeps the original context shape and data flow EXACTLY (pageNum, entities,
   entitySelected/updateEntity, the companies/* aliases the shared FB modals
   read, spotRates, dataForExport, mergedData, useInvestorReadyFinancial,
   messageApi, getAllUserEntities). Adds the .pn-* app shell (left sidebar +
   topbar + canvas) and a `goTo` helper, matching Financial Builder & Critical
   Finance. Page flow unchanged: 1 Entities -> 2 Products -> 3 Dashboard ->
   4 Export -> 5 Template Render. Template Render is intentionally untouched. */

import { useState, createContext, useEffect, useContext } from 'react';
import { message } from 'antd';
import {
  ApartmentOutlined, AppstoreOutlined, DashboardOutlined,
  ExportOutlined, LayoutOutlined, DownOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router';

import { fetchWithAccessTokenRetry } from '../helperFunctions.jsx';
import { ThemeToggle } from '../Navbar/Navbar.jsx';

import EntitiesPage from '../PSBB/EntitiesPage/EntitiesPage.jsx';
import ProductsPage from '../PSBB/ProductsPage/ProductsPage.jsx';
import Dashboard from './DashboardOverview/Dashboard.jsx';
import ExportDataPage from './ExportDataPage/ExportDataPage.jsx';
import { TemplateRenderWithContext } from '../TemplateRender/TemplateRender.jsx';

/* Reuse the Financial Builder design system. Tokens come from
   styles/colors_and_type.css (imported app-wide). */
import '../FinancialBuilder/financial-builder.css';

export const PSSBPageContext = createContext(null);

/* ------------------------------- shell pieces ------------------------------ */
function Sidebar() {
  const { pageNum, goTo, entitySelected, entities, dataForExport, mergedData } = useContext(PSSBPageContext);
  const hasProducts = !!entitySelected && (entitySelected.products?.length > 0);
  const nav = [
    { n: 1, label: 'Entities', icon: <ApartmentOutlined />, enabled: true },
    { n: 2, label: 'Products', icon: <AppstoreOutlined />, enabled: !!entitySelected },
    { n: 3, label: 'Dashboard', icon: <DashboardOutlined />, enabled: hasProducts },
    { n: 4, label: 'Export', icon: <ExportOutlined />, enabled: !!dataForExport },
    { n: 5, label: 'Template Render', icon: <LayoutOutlined />, enabled: !!(mergedData && mergedData.length) },
  ];
  return (
    <aside className="pn-side">
      <div className="pn-brand">
        <Link to="/" className="navbar-logo">
          <img className="navbar-logo-img" src="/imgs/logo.png" alt="PreNumbers" />
          <span className="navbar-logo-text">PreNumbers</span>
        </Link>
      </div>
      <div className="pn-nav-group-label">PSBB</div>
      {nav.map((item) => (
        <div key={item.n}
          className={"pn-nav-item" + (pageNum === item.n ? " active" : "")}
          style={item.enabled ? undefined : { opacity: 0.4, pointerEvents: 'none' }}
          onClick={() => item.enabled && goTo(item.n)}>
          {item.icon}<span>{item.label}</span>
        </div>
      ))}
      <div className="pn-nav-spacer" />
      <div className="pn-side-card">
        <div className="lbl">Active entity</div>
        <div className="val">{entitySelected ? entitySelected.name : 'None selected'}</div>
        <div className="lbl" style={{ marginTop: 8 }}>Workspace</div>
        <div className="val">{entities.length} entities</div>
      </div>
    </aside>
  );
}

function Topbar() {
  const { pageNum, entitySelected } = useContext(PSSBPageContext);
  const crumb = ['Entities', 'Products', 'Dashboard', 'Export', 'Template Render'][pageNum - 1] || 'Entities';
  return (
    <header className="pn-topbar">
      <div className="pn-crumbs"><span>PSBB</span><span className="sep">/</span><span className="cur">{crumb}</span></div>
      <div className="pn-topbar-spacer" />
      {entitySelected && (
        <div className="pn-company-switch"><span className="pn-company-dot" />{entitySelected.name}<DownOutlined style={{ fontSize: 11 }} /></div>
      )}
      <ThemeToggle />
    </header>
  );
}

function PageSwitcher() {
  const { pageNum } = useContext(PSSBPageContext);
  switch (pageNum) {
    case 1: return <EntitiesPage />;
    case 2: return <ProductsPage />;
    case 3: return <Dashboard />;
    case 4: return <ExportDataPage />;
    case 5: return <TemplateRenderWithContext context={PSSBPageContext} />;
    default: return <EntitiesPage />;
  }
}

/* --------------------------------- data ----------------------------------- */
async function getAllUserEntities(setEntities, setSpotRates, messageApi) {
  messageApi.info('Loading entities...');
  try {
    const response = await fetchWithAccessTokenRetry('/api/psbb/getEntities');
    const data = await response.json();
    if (!response.ok) {
      throw new Error('Failed to fetch entities', { cause: data.error });
    }
    setEntities(data.entities);
    setSpotRates(data.spotRates);
    messageApi.destroy();
    messageApi.success('Entities loaded successfully');
  } catch (error) {
    console.error('Error fetching user entities:', error);
    setEntities([]); // Clear entities on error
    messageApi.destroy();
    messageApi.error(error.cause || 'Error loading entities');
  }
}

function PSSBPage() {
  const [pageNum, setPageNum] = useState(1);
  const [messageApi, contextHolder] = message.useMessage();

  const [entities, setEntities] = useState([]);
  // This state is used to track the selected entity for navigation so in other pages it can be used to add more products
  const [entitySelectedID, setEntitySelectedID] = useState("");
  const [entitySelected, setEntitySelected] = useState(null);
  const [spotRates, setSpotRates] = useState(null);
  const [dataForExport, setDataForExport] = useState(null);
  const [mergedData, setMergedData] = useState([]);
  const [useInvestorReadyFinancial, setUseInvestorReadyFinancial] = useState(false);

  // adds a single updater that changes entitySelected and entities atomically
  const updateEntity = (updater) => {
    setEntitySelected(prev => {
      const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      setEntities(prevEntities => {
        const idx = prevEntities.findIndex(e => e.id === next.id);
        if (idx === -1) return prevEntities;
        const nextEntities = [...prevEntities];
        nextEntities[idx] = next;
        return nextEntities;
      });
      return next;
    });
  };

  const goTo = (n) => {
    setPageNum(n);
    const c = document.querySelector('.pn-canvas');
    if (c) c.scrollTop = 0;
  };

  // Must pass down both entities and companies for PSBB context
  const contextToUse = {
    pageNum,
    setPageNum,
    goTo,
    messageApi,
    entities,
    setEntities,
    entitySelectedID,
    setEntitySelectedID,
    entitySelected,
    setEntitySelected: updateEntity,
    spotRates,
    companies: entities,
    setCompanies: setEntities,
    companySelectedID: entitySelectedID,
    setCompanySelectedID: setEntitySelectedID,
    companySelected: entitySelected,
    setCompanySelected: updateEntity,
    dataForExport,
    setDataForExport,
    mergedData,
    setMergedData,
    useInvestorReadyFinancial,
    setUseInvestorReadyFinancial
  };

  useEffect(() => {
    getAllUserEntities(setEntities, setSpotRates, messageApi);
  }, [messageApi]);

  // Update the selected entity when the entitySelectedID changes
  useEffect(() => {
    if (entitySelectedID !== "-- Select a entity --") {
      const selectedEntity = entities.find(entity => entity.id === parseInt(entitySelectedID));
      setEntitySelected(selectedEntity);
    }
  }, [entitySelectedID, entities]);

  return (
    <>
      {contextHolder}
      <PSSBPageContext.Provider value={contextToUse}>
        <div className="pn-app">
          <Sidebar />
          <div className="pn-main">
            <Topbar />
            <div className="pn-canvas"><PageSwitcher /></div>
          </div>
        </div>
      </PSSBPageContext.Provider>
    </>
  );
}

export default PSSBPage;
